import { isUniversalSearchEnabled } from '../config/universalSearch'

const privateParams = new Set([
  'portfolio_id',
  'position_id',
  'stock_position_id',
  'stock_trade_id',
  'scenario_id',
  'saved_screen',
  'symbol',
  'q',
  'query',
])

/** Keep bookmarked private selections private even after rolling search back. */
export function isSearchReplayDisabled(): boolean {
  return (
    isUniversalSearchEnabled() ||
    (typeof window !== 'undefined' && searchSafeUrl(window.location.href) !== window.location.href)
  )
}

/** Removes search selections from analytics and diagnostic URLs; routing keeps its real URL. */
export function searchSafeUrl(value: string): string {
  try {
    const base = typeof window === 'undefined' ? 'https://optioneer.io' : window.location.origin
    const url = new URL(value, base)
    for (const key of [...url.searchParams.keys()]) {
      if (privateParams.has(key)) url.searchParams.delete(key)
      else if (key === 'returnTo')
        url.searchParams.set(key, searchSafeUrl(url.searchParams.get(key) || '/'))
    }
    url.pathname = url.pathname.replace(
      /(\/api\/(?:portfolios|positions|underlying-positions(?:\/stock-trades)?|stock-trades|scenario-lab|discovery\/saved-screens)\/)\b[0-9a-f-]{36}\b/gi,
      '$1[record]'
    )
    return /^https?:\/\//.test(value) ? url.toString() : `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '[URL redacted]'
  }
}

export function scrubSearchTelemetry<T>(value: T): T {
  if (typeof value === 'string') {
    if (/\/api\/search\//.test(value)) return '[search request]' as T
    if (/^(?:https?:\/\/|\/)/.test(value)) return searchSafeUrl(value) as T
    if (/https?:\/\/|\/api\//.test(value))
      return value.replace(/(?:https?:\/\/|\/api\/)[^\s"<>]+/g, searchSafeUrl) as T
    return value
  }
  if (Array.isArray(value)) return value.map(scrubSearchTelemetry) as T
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, scrubSearchTelemetry(item)])
    ) as T
  }
  return value
}

export function searchBreadcrumbAllowed(
  category: string | undefined,
  data: unknown,
  target?: EventTarget | null
): boolean {
  if (target instanceof Element && target.closest('[data-private-search]')) return false
  if (JSON.stringify(data ?? {}).includes('/api/search/')) return false
  if (
    document.querySelector('[data-private-search]') &&
    (category?.startsWith('ui.') || category === 'console')
  )
    return false
  return true
}
